import { useDebounceState } from "@prismicio/editor-support/React"
import { slugify } from "@prismicio/editor-support/String"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import {
  AnimatedElement,
  Badge,
  ErrorBoundary,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  Text
} from "@prismicio/editor-ui"
import type { UIDContent } from "@prismicio/types-internal/lib/content"
import type { UID as UIDType } from "@prismicio/types-internal/lib/customtypes"
import { type ClipboardEvent, Suspense, useContext } from "react"

import { getDocumentUrl } from "../core/document"
import {
  createSearchParams,
  type SearchDocumentNames,
  searchDocuments
} from "../core/service"
import { getConflictingUidDocuments, UIDContext } from "../core/UID"
import { throwConfigError, useEditorConfig } from "../EditorConfig"
import { FieldTextInput } from "./FieldTextInput"
import { getFieldLabel, getFieldPlaceholder } from "./utils"

interface UIDFieldProps {
  id: string
  content?: UIDContent
  field: UIDType
  onContentChange: (content?: UIDContent) => void
}

export function UIDField(props: UIDFieldProps) {
  const { id, field, content, onContentChange } = props

  const uid = useDebounceState({
    state: content?.value ?? "",
    synchronize: true
  })

  const { onEditUID } = useContext(UIDContext)

  function onValueChange(newUID: string) {
    onEditUID()
    const slug = slugify(newUID)
    onContentChange(updateContent(slug))
    uid.set(slug)
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    onValueChange(event.clipboardData.getData("Text").trim())
    event.preventDefault()
  }

  return (
    <Field>
      <FieldIcon name="tag" />
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <FieldTextInput
          id={id}
          variant="success"
          placeholder={getFieldPlaceholder(field)}
          value={uid.value}
          onPaste={onPaste}
          onValueChange={onValueChange}
          endAdornment={
            <AnimatedElement enterDuration={150}>
              <UidEndAdornment uid={uid.debouncedValue} />
            </AnimatedElement>
          }
        />
      </FieldControl>
    </Field>
  )
}

interface UidEndAdornmentProps {
  uid?: string
}

function UidEndAdornment(props: UidEndAdornmentProps) {
  const { uid } = props

  const config = useEditorConfig()

  if (!uid) return <Badge color="tomato" title="Required" size="medium" />

  if (!config.uidField) return null

  return (
    <ErrorBoundary>
      <Suspense>
        <NonUniqueBadge uid={uid} />
      </Suspense>
    </ErrorBoundary>
  )
}

interface NonUniqueBadgeProps {
  uid: string
}

function NonUniqueBadge(props: NonUniqueBadgeProps) {
  const documentWithConflictingUid = useIsUniqueUrl(props)

  const config = useEditorConfig()

  if (!documentWithConflictingUid.documentId) return null

  const documentUrl = getDocumentUrl({
    status: documentWithConflictingUid.status,
    releaseId: documentWithConflictingUid.releaseId,
    documentId: documentWithConflictingUid.documentId,
    versionId: documentWithConflictingUid.versionId,
    baseUrl: config.baseUrl
  })

  const badgeContent = (
    <>
      <Text color="tomato11" variant="small">
        Value already used in{" "}
      </Text>
      <Text color="tomato11" variant="small" underline>
        another document
      </Text>
    </>
  )

  return (
    <Badge
      color="tomato"
      title={badgeContent}
      onClick={() => window.open(documentUrl, "_blank")}
      size="medium"
    />
  )
}

function updateContent(value: string): UIDContent | undefined {
  return value === "" ? undefined : { __TYPE__: "UIDContent", value }
}

cache.registerFetcherConfig(searchDocuments, {
  revalidationStrategy: { type: "swr", onFocus: true }
})

interface useIsUniqueUrlProps {
  uid: string
}

function useIsUniqueUrl(props: useIsUniqueUrlProps) {
  const { uid } = props
  const config = useEditorConfig()

  if (!config.uidField) throwConfigError("uidField")

  const { coreApiBaseUrl, authStrategy, customTypeId, localeId, documentId } =
    config

  const searchTerm = new URLSearchParams(
    createSearchParams<SearchDocumentNames>([{ name: "uid", values: [uid] }])
  ).toString()

  const documentsWithUid = useRequest(searchDocuments, [
    coreApiBaseUrl,
    authStrategy,
    searchTerm
  ])

  const documents = documentsWithUid.results

  const conflictingDocuments = getConflictingUidDocuments({
    customTypeId,
    languageId: localeId,
    documentsWithUid: documents,
    documentId,
    uid
  })

  const conflictingDocument = conflictingDocuments[0]

  if (!conflictingDocument) return {}

  return {
    documentId: conflictingDocument.id,
    versionId: conflictingDocument.versions[0]?.version_id,
    status: conflictingDocument.versions[0]?.status,
    releaseId:
      conflictingDocument.versions[0]?.status === "release"
        ? conflictingDocument.versions[0]?.release_id
        : undefined
  }
}
